// ─── Dynamic add/remove text input list ─────────────────────────────────────
// Used for headlines, descriptions, display URL paths, etc.
// Min/max enforced. Character counters on each input.

import { Plus, X } from 'lucide-react';
import { InlineIssue } from '../../ad-details/shared';
import type { FieldIssue } from './types';

interface DynamicTextFieldArrayProps {
  label: string;
  hint?: string;
  values: string[];
  onChange: (values: string[]) => void;
  min: number;
  max: number;
  charLimit: number;
  fieldIssuePrefix?: string;
  fieldIssues?: Record<string, FieldIssue>;
  showErrors?: boolean;
  showWarnings?: boolean;
  placeholder?: string;
  /** Section-level issue (e.g. "At least 1 headline required") — shown at bottom of section */
  sectionIssue?: FieldIssue;
}

export function DynamicTextFieldArray({
  label,
  hint,
  values,
  onChange,
  min,
  max,
  charLimit,
  fieldIssuePrefix,
  fieldIssues,
  showErrors = false,
  showWarnings = false,
  placeholder,
  sectionIssue,
}: DynamicTextFieldArrayProps) {
  function updateAt(index: number, value: string) {
    const next = [...values];
    next[index] = value;
    onChange(next);
  }

  function addItem() {
    if (values.length >= max) return;
    onChange([...values, '']);
  }

  function removeAt(index: number) {
    if (values.length <= min) return;
    onChange(values.filter((_, i) => i !== index));
  }

  const hasSectionError = !!(sectionIssue?.errors.length && showErrors);
  const hasSectionWarning = !!(sectionIssue?.warnings.length && showWarnings);
  const sectionBorderColor = hasSectionError
    ? 'border-red-300'
    : hasSectionWarning
      ? 'border-amber-300'
      : 'border-slate-200';

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            {label}
          </p>
          <span className="text-[10px] text-slate-300 tabular-nums">
            {min === max ? '' : `(${min}–${max}) · `}Max {charLimit}
            {hint ? ` · ${hint}` : ''}
          </span>
        </div>
        {min !== max && (
          <span className="text-[10px] text-slate-300 tabular-nums">
            {values.length}/{max}
          </span>
        )}
      </div>

      <div className={`border-l-2 ${sectionBorderColor} pl-3 py-1 space-y-2`}>
        {values.length === 0 && values.length < max && (
          <button
            type="button"
            onClick={addItem}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0014cc] transition-colors py-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Add {label.replace(/s$/, '')}
          </button>
        )}
        {values.map((value, index) => {
          const issueKey = fieldIssuePrefix ? `${fieldIssuePrefix}_${index}` : undefined;
          const issue = issueKey && fieldIssues ? fieldIssues[issueKey] : undefined;
          const over = value.length > charLimit;
          const borderClass =
            !!issue?.errors.length && showErrors
              ? 'border-red-400 focus:border-red-400 focus:ring-red-100'
              : !!issue?.warnings.length && showWarnings
                ? 'border-amber-400 focus:border-amber-400 focus:ring-amber-100'
                : '';

          return (
            <div key={index} className="flex items-start gap-2">
              <div className="flex-1 space-y-1">
                <div className="relative">
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => updateAt(index, e.target.value)}
                    placeholder={placeholder ?? `${label.replace(/s$/, '')} ${index + 1}...`}
                    className={`w-full px-3 py-2 pr-14 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 transition-all ${
                      borderClass ||
                      'border-slate-300 focus:ring-[#0014cc]/10 focus:border-[#0014cc]/50'
                    }`}
                  />
                  <span
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs tabular-nums ${over ? 'text-amber-600 font-medium' : 'text-slate-400'}`}
                  >
                    {value.length}/{charLimit}
                  </span>
                </div>
                {/* Per-input warnings/errors — full width, left-aligned below input */}
                <InlineIssue
                  issue={issue}
                  showErrors={showErrors}
                  showWarnings={showWarnings}
                  inline
                />
                {values.length < max && index === values.length - 1 && (
                  <button
                    type="button"
                    onClick={addItem}
                    className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0014cc] transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add {label.replace(/s$/, '')}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeAt(index)}
                disabled={values.length <= min}
                className="w-7 h-7 mt-1 flex items-center justify-center rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-20 disabled:hover:text-slate-400 disabled:hover:bg-transparent flex-shrink-0"
                title="Remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Section-level error/warning — left-aligned, tight below the border strip */}
      {hasSectionError &&
        sectionIssue!.errors.map((msg, i) => (
          <p key={`e${i}`} className="text-xs text-red-600 mt-1">
            ✕ {msg}
          </p>
        ))}
      {hasSectionWarning &&
        !hasSectionError &&
        sectionIssue!.warnings.map((msg, i) => (
          <p key={`w${i}`} className="text-xs text-amber-600 mt-1">
            ⚠ {msg}
          </p>
        ))}
    </div>
  );
}
